import * as React from 'react'
import { StoreProps, castingStoreGetDurationForAllowedPattern } from '../../../../services/stores/casting.store'
import { FloatingContainerTitle, InfoBoxError, PrimaryButton } from '../casting-styled.component'
import SelectableListComponent from '../../../shared/selectable-list.component'
import { UnserializedMultiOpeningHours, openableRoundTimeUpper } from '../../../../services/utils/openables.service'
import { CastingSlot } from '../../../../services/models/castings.service'
import { moment } from '../../../../services/utils/moment.service'
import { formatDurationFromHours } from '../../../../services/utils/utils.service'
import { I18nService } from '../../../../services/utils/i18n.service'

type Props = StoreProps & {
  availableAsapOffers: number[]
  multiOpeningHoursUnsafeMidnight: UnserializedMultiOpeningHours & { reduced: true }
}

const i18nScoped = new I18nService(require('./casting-start-date-asap.component.yml'))

export default function CastingStartDateAsapComponent ({ store, data, availableAsapOffers, multiOpeningHoursUnsafeMidnight }: Props) {
  const { offeredService: os, slots, asapOffer } = data

  const setChangeAsapOffer = React.useCallback((newAsapOffer: number) => {
    // from is now + 15 minutes rounded to upper step (it lets some time to customer to complete the casting)
    const from = openableRoundTimeUpper(new Date(), os.duration_step / 3600)

    // If a slot of `duration` ends before `from + asapOffer + duration`, this means it starts before ̀from + asapOffer̀.
    // Karim asks some extra margin, so the employee can start a bit later (+15 minutes).
    // Because `from` is also rounded to upper step, it introduces a cumulated margin of 30 minutes.
    const until = from.clone().add(newAsapOffer, 'hours').add(castingStoreGetDurationForAllowedPattern(data) + 900, 'second')
    const newSlots: CastingSlot[] = [{ ...slots[0], start_date: moment(from).format('YYYY-MM-DD'), allowed_patterns: [] }]
    newSlots[0].allowed_patterns = [{ day_id: from.day(), from_hour: from.format('HH:mm'), until_hour: until.format('HH:mm'), additional_days: null as number }]
    store.set({ asapOffer: newAsapOffer, slots: newSlots })
    store.nextFocus({ closeOnMobile: true })
  }, [data, os.duration_step, slots, store])

  const disableAsap = React.useCallback(() => {
    store.set({ offeredService: { ...os, asap: false, asap_only: false }, asapOffer: null }, { runCallbacks: true, runResets: false })
  }, [os, store])

  return <>
    { /** Render asap UI if any choice available (customer select an asap offer in the list) */ }
    { availableAsapOffers.length > 0 && <React.Fragment>
      <FloatingContainerTitle>{ i18nScoped.t('start_time_limit') }</FloatingContainerTitle>

      <SelectableListComponent
        data={ availableAsapOffers.concat(os.asap_only ? [] : [-1]) }
        name="asapOffer"
        value={ asapOffer }
        formatter={ newVal => newVal === -1 ? i18nScoped.t('let_me_choose_any_date') : i18nScoped.t('in_x_hours', { count: newVal }) }
        onChange={ newVal => newVal === -1 ? disableAsap() : setChangeAsapOffer(newVal) } />
    </React.Fragment> }

    { /** Render an asap offer available, with opening hours table and a button disabling asap mode */ }
    { os.asap_only && os.rentable_unit_of_work !== 'day' && !availableAsapOffers.length && <React.Fragment>
      <InfoBoxError style={{ marginBottom: '1em' }}>{ i18nScoped.t('service_closed_for_next_hours', { count: os.asap_offers.slice().pop() }) }</InfoBoxError>
      <FloatingContainerTitle>{ i18nScoped.t('day_and_time_of_opening') }</FloatingContainerTitle>

      <table style={{ width: '100%' }}>
        <tbody>
          { ([1, 2, 3, 4, 5, 6, 7] as const).map(dayId => <tr key={ dayId }>
            <td style={{ padding: '0.5em', textTransform: 'capitalize' }}>{ moment().day(dayId).format('dddd') }</td>
            <td style={{ padding: '0.5em' }}>
              { multiOpeningHoursUnsafeMidnight[dayId].length === 0 && i18nScoped.t('closed') }
              { multiOpeningHoursUnsafeMidnight[dayId].map((interval, index) => <div key={ index }>{ formatDurationFromHours(interval[0]) } - { formatDurationFromHours(interval[1]) }</div>) }
            </td>
          </tr>) }
        </tbody>
      </table>

      <PrimaryButton id="js-test-disable-asap" style={{ display: 'block', marginTop: '2em' }} onClick={ disableAsap }>
        { i18nScoped.t('book_after_x_hours', { count: os.asap_offers.slice().pop() }) }
      </PrimaryButton>
    </React.Fragment> }
  </>
}